import dayjs from 'dayjs';

import type {
  FocusMatchLiveOps,
  FocusMatchLiveOpsCard,
  FocusMatchLiveOpsResponse,
  FocusMatchRule,
  FocusMatchRuleResponse,
  FocusMatchSchedule,
  FocusMatchScheduleListResponse,
  MatchLite,
  MatchesByStage,
  RawMatchesByStage,
  RuleDraft,
  ScheduleDraft,
  TeamInfo,
} from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function ensureArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

export function normalizeLiveOps(raw: FocusMatchLiveOpsResponse | unknown): FocusMatchLiveOps {
  if (!raw) {
    return { cards: [] };
  }

  if (Array.isArray(raw)) {
    return { cards: raw as FocusMatchLiveOpsCard[] };
  }

  if (!isRecord(raw)) {
    return { cards: [] };
  }

  if (Array.isArray(raw.cards)) {
    return { cards: raw.cards as FocusMatchLiveOpsCard[] };
  }

  if (isRecord(raw.liveOps)) {
    return {
      cards: ensureArray<FocusMatchLiveOpsCard>(raw.liveOps.cards),
    };
  }

  if (Array.isArray(raw.data)) {
    return { cards: raw.data as FocusMatchLiveOpsCard[] };
  }

  if (isRecord(raw.data)) {
    return {
      cards: ensureArray<FocusMatchLiveOpsCard>(raw.data.cards),
    };
  }

  return { cards: [] };
}

export function normalizeSchedules(
  raw: FocusMatchScheduleListResponse | unknown
): FocusMatchSchedule[] {
  if (Array.isArray(raw)) {
    return raw;
  }

  if (!isRecord(raw)) {
    return [];
  }

  if (Array.isArray(raw.schedules)) {
    return raw.schedules as FocusMatchSchedule[];
  }

  if (Array.isArray(raw.items)) {
    return raw.items as FocusMatchSchedule[];
  }

  if (Array.isArray(raw.list)) {
    return raw.list as FocusMatchSchedule[];
  }

  if (Array.isArray(raw.rows)) {
    return raw.rows as FocusMatchSchedule[];
  }

  if (Array.isArray(raw.data)) {
    return raw.data as FocusMatchSchedule[];
  }

  if (isRecord(raw.data) && Array.isArray(raw.data.schedules)) {
    return raw.data.schedules as FocusMatchSchedule[];
  }

  return [];
}

export function normalizeSchedule(raw: unknown): FocusMatchSchedule | null {
  if (!raw) return null;

  if (!isRecord(raw)) return null;

  if (isRecord(raw.schedule)) {
    return raw.schedule as FocusMatchSchedule;
  }

  if (isRecord(raw.data)) {
    if (isRecord(raw.data.schedule)) {
      return raw.data.schedule as FocusMatchSchedule;
    }

    if ('id' in raw.data) {
      return raw.data as FocusMatchSchedule;
    }
  }

  if ('id' in raw) {
    return raw as FocusMatchSchedule;
  }

  return null;
}

export function normalizeRule(raw: FocusMatchRuleResponse | unknown): FocusMatchRule | null {
  if (!raw || !isRecord(raw)) {
    return null;
  }

  if (isFocusMatchRule(raw)) {
    return raw;
  }

  if (isRecord(raw.rule) && isFocusMatchRule(raw.rule)) {
    return raw.rule;
  }

  if (isRecord(raw.data)) {
    if (isFocusMatchRule(raw.data)) {
      return raw.data;
    }

    if (isRecord(raw.data.rule) && isFocusMatchRule(raw.data.rule)) {
      return raw.data.rule;
    }
  }

  return null;
}

function isFocusMatchRule(value: Record<string, unknown>): value is FocusMatchRule {
  return (
    'openHoursBeforeKickoff' in value ||
    'cutoffMinutesBeforeKickoff' in value ||
    'perUserStakeCap' in value ||
    'perMatchStakeCap' in value ||
    'defaultSortWeight' in value ||
    'pillZh' in value ||
    'pillEn' in value ||
    'enabled' in value
  );
}

export function normalizeMatchesByStage(raw?: RawMatchesByStage | null): MatchesByStage {
  return {
    group: ensureArray<MatchLite>(raw?.group),
    roundOf32: ensureArray<MatchLite>(raw?.roundOf32 ?? raw?.round_of_32),
    roundOf16: ensureArray<MatchLite>(raw?.roundOf16 ?? raw?.round_of_16),
    quarterFinal: ensureArray<MatchLite>(raw?.quarterFinal ?? raw?.quarter_final),
    semiFinal: ensureArray<MatchLite>(raw?.semiFinal ?? raw?.semi_final),
    thirdPlace: ensureArray<MatchLite>(raw?.thirdPlace ?? raw?.third_place),
    final: ensureArray<MatchLite>(raw?.final),
  };
}

export function flattenMatchesByStage(matchesByStage?: MatchesByStage | null): MatchLite[] {
  if (!matchesByStage) return [];

  return [
    ...ensureArray<MatchLite>(matchesByStage.group),
    ...ensureArray<MatchLite>(matchesByStage.roundOf32),
    ...ensureArray<MatchLite>(matchesByStage.roundOf16),
    ...ensureArray<MatchLite>(matchesByStage.quarterFinal),
    ...ensureArray<MatchLite>(matchesByStage.semiFinal),
    ...ensureArray<MatchLite>(matchesByStage.thirdPlace),
    ...ensureArray<MatchLite>(matchesByStage.final),
  ];
}

export function toNumber(value?: number | string | null) {
  const num = Number(value ?? 0);

  return Number.isFinite(num) ? num : 0;
}

export function formatInt(value?: number | string | null) {
  return new Intl.NumberFormat('en-US').format(toNumber(value));
}

export function formatUsdc(value?: number | string | null) {
  return `$${new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(toNumber(value))}`;
}

export function formatDateTime(value?: string | null) {
  if (!value) return '--';

  return dayjs(value).format('YYYY-MM-DD HH:mm');
}

export function getTeamName(team?: TeamInfo | null, locale: string = 'en') {
  if (!team) return '';
  if (locale === 'zh' || locale === 'cn') {
    return team.nameZh || team.code || '';
  }

  return team.nameEn || team.code || '';
}

export function getMatchTitle(match?: MatchLite | null) {
  if (!match) return '--';

  const home = match.homeTeam ? getTeamName(match.homeTeam) : match.homePlaceholder || '--';
  const away = match.awayTeam ? getTeamName(match.awayTeam) : match.awayPlaceholder || '--';

  return `${home} vs ${away}`;
}

export function getMatchOptionLabel(match: MatchLite, t?: Translate) {
  const stage = match.stage || match.stageLabel || '--';

  let midText = '--';
  if (stage === 'group') {
    midText = t
      ? t('module.worldCup.focusMatch.stage.group', { group: match.groupName || '--' })
      : `Group ${match.groupName || '--'}`;
  } else {
    midText = t
      ? t(`module.worldCup.focusMatch.stage.${stage}`)
      : stage.charAt(0).toUpperCase() + stage.slice(1).replace(/_/g, ' ');
  }

  return `${getMatchTitle(match)} · ${midText} · ${formatDateTime(match.kickoffAt)}`;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function getMatchStatusText(match?: MatchLite | null, t?: Translate) {
  if (!match) return '--';
  if (match.status === 'scheduled') {
    return formatDateTime(match.kickoffAt);
  }

  if (match.status === 'live') {
    return t
      ? t('module.worldCup.focusMatch.match.liveMinute', { minute: match.liveMinute ?? '--' })
      : `Live ${match.liveMinute ?? '--'}′`;
  }

  if (t) {
    return t(`module.worldCup.focusMatch.match.status.${match.status}`);
  }

  return match.status.charAt(0).toUpperCase() + match.status.slice(1);
}

export function toRuleDraft(rule: FocusMatchRule): RuleDraft {
  return {
    openHoursBeforeKickoff: toNumber(rule.openHoursBeforeKickoff),
    cutoffMinutesBeforeKickoff: toNumber(rule.cutoffMinutesBeforeKickoff),
    perUserStakeCap: toNumber(rule.perUserStakeCap),
    perMatchStakeCap: toNumber(rule.perMatchStakeCap),
  };
}

export function toScheduleDraft(item: FocusMatchSchedule): ScheduleDraft {
  return {
    id: String(item.id),
    match: {
      ...item.match,
      liveMinute: item.liveMinute,
    },
    poolUsdc: String(item.poolUsdc ?? ''),
    originalPoolUsdc: String(item.poolUsdc ?? ''),
    sortWeight: toNumber(item.sortWeight),
    originalSortWeight: toNumber(item.sortWeight),
    isLocked: Boolean(item.isLocked),
    lockedReason: item.lockedReason,
    liveMinute: item.liveMinute,
    cardState: item.cardState,
    totalVouchers: toNumber(item.totalVouchers),
    userVouchers: toNumber(item.userVouchers),
    mirrorVouchers: toNumber(item.mirrorVouchers),
    whitelistVouchers: toNumber(item.whitelistVouchers),
    recoveryRatio: toNumber(item.recoveryRatio),
    originalRecoveryRatio: toNumber(item.recoveryRatio),
  };
}

export function isRuleDraftEqual(left?: RuleDraft | null, right?: RuleDraft | null) {
  if (!left || !right) return left === right;

  return (
    left.openHoursBeforeKickoff === right.openHoursBeforeKickoff &&
    left.cutoffMinutesBeforeKickoff === right.cutoffMinutesBeforeKickoff &&
    left.perUserStakeCap === right.perUserStakeCap &&
    left.perMatchStakeCap === right.perMatchStakeCap
  );
}

export function getChangedSchedulePayload(row: ScheduleDraft) {
  const payload: {
    poolUsdc?: string;
    sortWeight?: number;
    recoveryRatio?: number;
  } = {};

  if (String(row.poolUsdc) !== String(row.originalPoolUsdc)) {
    payload.poolUsdc = String(row.poolUsdc);
  }

  if (Number(row.sortWeight) !== Number(row.originalSortWeight)) {
    payload.sortWeight = Number(row.sortWeight);
  }

  if (Number(row.recoveryRatio) !== Number(row.originalRecoveryRatio)) {
    payload.recoveryRatio = Number(row.recoveryRatio);
  }

  return payload;
}

export function hasScheduleChanged(row: ScheduleDraft) {
  return Object.keys(getChangedSchedulePayload(row)).length > 0;
}

export function getErrorMessage(error: unknown) {
  if (!error) return '';

  if (typeof error === 'string') return error;

  if (error instanceof Error) return error.message;

  if (isRecord(error)) {
    if (typeof error.message === 'string') {
      return error.message;
    }

    if (isRecord(error.response) && isRecord(error.response.data)) {
      const responseData = error.response.data;

      if (typeof responseData.message === 'string') {
        return responseData.message;
      }

      if (typeof responseData.error === 'string') {
        return responseData.error;
      }
    }

    if (typeof error.error === 'string') {
      return error.error;
    }
  }

  return String(error);
}
